import type { stats } from "@app/domain";
import type { Member } from "@app/protocol";
import { botAvatar } from "./botAvatar";
import { FRAME_HOLE_SHARE, RankFrameImage } from "./RankFrame";

/** Solid colours from the design handoff; the initial is white on each. */
const COLORS = [
  "#3b6fd6",
  "#c2417a",
  "#2f9e6b",
  "#d97706",
  "#7c3aed",
  "#0891b2",
  "#0f766e",
  "#be123c",
  "#4338ca",
  "#a21caf",
  "#15803d",
  "#b45309",
];

function hash(text: string): number {
  let h = 0;
  for (const ch of text) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h;
}

/**
 * A bot's picture, the Discord picture, else an initial on a colour that sticks to the user.
 * With a rank frame the frame fills `size` and the avatar sits in its hole (review 0014).
 */
export function Avatar({
  member,
  size,
  className = "",
  rank = null,
}: {
  rank?: stats.RankFrame | null;
  /** `bot` comes with a seat; members from elsewhere (hosts, the leaderboard) are people. */
  member: (Pick<Member, "userId" | "name" | "avatar"> & { bot?: boolean }) | null;
  size: number;
  className?: string;
}) {
  if (rank !== null && member !== null) {
    const inner = Math.round(size * FRAME_HOLE_SHARE);
    return (
      <span
        className={`relative flex flex-none items-center justify-center ${className}`}
        style={{ width: size, height: size }}
      >
        <Avatar member={member} size={inner} />
        <RankFrameImage rank={rank} avatarSize={inner} />
      </span>
    );
  }
  const style = { width: size, height: size };
  if (member === null) {
    return (
      <span
        aria-hidden="true"
        className={`block flex-none rounded-full border-[1.5px] border-dashed border-ink/25 ${className}`}
        style={style}
      />
    );
  }
  const picture = member.bot === true ? botAvatar(member.userId) : member.avatar;
  if (picture !== null) {
    return (
      <img
        src={picture}
        alt=""
        className={`block flex-none rounded-full object-cover ${className}`}
        style={style}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`flex flex-none items-center justify-center rounded-full font-ui font-extrabold text-ink ${className}`}
      style={{
        ...style,
        background: COLORS[hash(member.userId) % COLORS.length],
        fontSize: size * 0.37,
      }}
    >
      {member.name.slice(0, 1).toLocaleUpperCase()}
    </span>
  );
}
